import { FormEvent, useCallback, useEffect, useState, MouseEvent } from 'react';
import useSessionStorage from '@gumtree/ui-library/src/common-hooks/use-session-storage';
import { CONVERSATION_SERVICE_TIMEOUT_MS } from '@gumtree/shared/src/constants/message-centre';
import { L1Category } from '@gumtree/shared/src/types/client-data';
import { useModal } from '@gumtree/shell/src/use-modal';
import { useDispatch } from 'react-redux';
import { NameValuePair } from '@gumtree/shared/src/model/generic.model';
import { navigate } from '@gumtree/shell/src/common/common-actions';
import getRequestId from '@gumtree/shell/src/request-id';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import type { MessageProps } from './message';

// keep the original logic
export const advertIdStorageKey = 'adId';
export const messageStorageKey = 'textContent';
export const phoneStorageKey = 'phoneNumberContent';

export const phoneRegex = /^[0-9+-\s]*$/;
export const phoneRegexComplex = /^(\+44|0)\s*(?:[1-9]\s*){1}(?:\d\s*){9}$|^$/;

export type GetPlaceholderTextOpts = {
    isMobile: boolean;
    seoName: L1Category | undefined;
    sellerName?: string;
};

export function getPlaceholderText(opts: GetPlaceholderTextOpts) {
    if (opts.isMobile) {
        switch (opts.seoName) {
            case 'cars-vans-motorbikes':
                return "Hi, is this still available? I'm interested! thanks!";
            case 'for-sale':
            case 'pets':
                return 'Hi, is this still available? Thanks!';
        }
    }
    return `Hi ${opts.sellerName},

I'm interested in your item. Is this still available?

Thanks`;
}

export const useStorePhoneNumber = (regex: RegExp, defaultPhoneNumber: string, required: boolean = false) => {
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [phoneError, setPhoneError] = useState<string>('');
    const [canValidate, setCanValidate] = useState(false);
    const [savedPhone, storePhone] = useSessionStorage(phoneStorageKey, defaultPhoneNumber);

    const validatePhoneValue = useCallback((value: string) => {
        // empty
        if (required && (!value || value.trim() === '')) {
            setPhoneError('Phone number is required');
            return false;
        }
        // empty and not required
        if (!value || value.trim() === '') {
            setPhoneError('');
            return true;
        }
        // format validation
        const isValid = regex.test(value);
        setPhoneError(isValid ? '' : 'Invalid phone number');
        return isValid;
    }, [regex, required]);

    const updatePhone = useCallback(({ currentTarget: { value } }: FormEvent<HTMLInputElement>) => {
        storePhone(value);
        if (canValidate) {
            setIsPhoneValid(validatePhoneValue(value));
        }
    }, [canValidate, validatePhoneValue, storePhone]);

    const validatePhone = useCallback(() => {
        setCanValidate(true);
        const valid = validatePhoneValue(savedPhone || '');
        setIsPhoneValid(valid);
        return valid;
    }, [savedPhone, validatePhoneValue]);

    return {
        savedPhone,
        storePhone,
        updatePhone,
        validatePhone,
        onPhoneInputBlur: validatePhone,
        isPhoneValid,
        phoneError,
    }
}

type UseStoreMessageOpts = {
    placeholder: string;
    useStore?: boolean;
};
export const useStoreMessage = (advertId: string, opts: UseStoreMessageOpts) => {
    const [message, setMessage] = useState(opts.placeholder);
    const [savedMessage, storeMessage] = useSessionStorage(messageStorageKey);
    const [savedAdvertId, storeAdvertId] = useSessionStorage(advertIdStorageKey);
    const [isInitialized, setIsInitialized] = useState(false);

    const updateMessage = useCallback(
        ({ currentTarget: { value } }: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            opts.useStore ? storeMessage(value) : setMessage(value);
        },
        [opts.useStore]
    );

    const clearStore = useCallback(() => {
        storeMessage(null);
        storeAdvertId(null);
    }, []);

    useEffect(() => {
        if (opts.useStore) {
            if (!savedAdvertId || savedAdvertId !== advertId) {
                storeMessage(opts.placeholder || null);
            }
            storeAdvertId(advertId);   
        }
        setIsInitialized(true);
    }, [advertId, savedAdvertId, opts.placeholder, opts.useStore]);

    return {
        isInitialized,
        savedMessage: opts.useStore ? savedMessage : message,
        updateMessage,
        clearStore,
    };
};

export const useClearStoreMessage = () => {
    const [, storeMessage] = useSessionStorage(messageStorageKey);
    const [, storeAdvertId] = useSessionStorage(advertIdStorageKey);
    const clearStore = useCallback(() => {
        storeMessage(null);
        storeAdvertId(null);
    }, []);
    return {
        clearStore,
    };
};

export const autoResizeTextArea = (textarea: HTMLTextAreaElement | null, maxLines: number) => {
    if (!textarea) return;

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
    const padding =
        parseInt(window.getComputedStyle(textarea).paddingTop) +
        parseInt(window.getComputedStyle(textarea).paddingBottom);

    const lines = Math.floor((scrollHeight - padding) / lineHeight);

    if (lines <= maxLines) {
        textarea.style.height = scrollHeight + 'px';
        textarea.style.overflowY = 'hidden';
    } else {
        const maxHeight = lineHeight * maxLines + padding;
        textarea.style.height = maxHeight + 'px';
        textarea.style.overflowY = 'auto';
    }
};

export const appendParams = (url: string, params: Record<string, string>) => {
    const urlObj = new URL(url, window.location.origin);
    for (const key in params) {
        urlObj.searchParams.set(key, params[key]);
    }
    return urlObj.toString();
};

export type SendMessageParams = {
    message: string;
    phoneNumber?: string;
    [key: string]: any;
};

export const sendMessage = async (opts: {
    sendReplyUrl: string;
    advertId: string;
    params: SendMessageParams;
    token: string;
}) => {
    const resp = (await Promise.race([
        new Promise((_, reject) =>
            setTimeout(() => reject('Response timed out'), CONVERSATION_SERVICE_TIMEOUT_MS)
        ),
        fetch(opts.sendReplyUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                X_CSRF_TOKEN: opts.token,
            },
            body: JSON.stringify({
                advertId: opts.advertId,
                ...opts.params,
            }),
        }),
    ])) as Response;

    const payload = await resp.json();
    if (payload.errors) {
        throw { errors: payload.errors.slice(0, 1), payload };
    } else if (!payload.replyConfirmationUrl) {
        throw { payload };
    }

    return payload as {
        replyConfirmationUrl: string;
        errors: null;
    };
};

export type TrackOtherParams = Omit<
    GA4.ContactSellerMessageEvent,
    | 'event'
    | 'formName'
    | 'formStep'
    | 'formValidation'
    | 'formError'
    | 'requestId'
    | 'contactASellerMethod'
    | 'isProSellerAccount'
>;
export const useSendMessage = <T extends SendMessageParams>({
    sendReplyUrl,
    advertId,
    token,
    userLoggedIn,
    outOfHours,
    proAccount,
    sendMessageSuccessFunc,
}: {
    sendReplyUrl: string;
    advertId: string;
    token: string;
    userLoggedIn: boolean;
    outOfHours?: boolean;
    proAccount: boolean;
    sendMessageSuccessFunc?: () => void;
}) => {
    const { openDialog } = useModal();
    const dispatch = useDispatch();
    const [sendState, setSendState] = useState<MessageProps['sendState']>('unsent');
    const [errors, setErrors] = useState<NameValuePair[]>([]);

    const handleSendMessage = async (
        e: MouseEvent<HTMLButtonElement>,
        params: T,
        trackParams: TrackOtherParams
    ) => {
        if (!userLoggedIn) {
            const isFromMotorsFeed = trackParams.listingPartner === 'motors';
            openDialog(e, LoginModalType.EMAIL, true, isFromMotorsFeed ? `/reply-message/${advertId}` : undefined);
            return;
        }

        const send = async () => {
            setSendState('sending');

            try {
                const { replyConfirmationUrl } = await sendMessage({
                    advertId,
                    sendReplyUrl,
                    token,
                    params,
                });

                trackPermutiveCustomEvent(window, PermutiveEventNames.R2S_EMAIL_BEGIN, {}, true);

                trackGA4PreNav<GA4.ContactSellerMessageEvent>({
                    event: 'contact_a_seller',
                    contactASellerMethod: 'send a message',
                    formName: 'contact a seller',
                    formStep: undefined,
                    formValidation: 'success',
                    formError: undefined,
                    requestId: getRequestId(),
                    isProSellerAccount: proAccount,
                    ...trackParams,
                });

                sendMessageSuccessFunc?.();

                let nextUrl = replyConfirmationUrl;
                if (outOfHours) {
                    nextUrl = appendParams(replyConfirmationUrl, { outofhours: 'true' });
                }
                dispatch(navigate(nextUrl));
            } catch (e) {
                const errorMessage = `${
                    typeof e === 'string' ? e : 'Unexpected error'
                }, please try again`;

                setErrors(
                    e.errors || [
                        {
                            name: 'sendError',
                            value: errorMessage,
                        },
                    ]
                );

                trackGA4Event<GA4.ContactSellerMessageEvent>({
                    event: 'contact_a_seller',
                    contactASellerMethod: 'send a message',
                    formName: 'contact a seller',
                    formStep: undefined,
                    formValidation: 'failure',
                    formError: errorMessage,
                    isProSellerAccount: proAccount,
                    ...trackParams,
                });

                console.error(e);
            } finally {
                setSendState('sent');
            }
        };
        await send();
    };

    return {
        sendMessage: handleSendMessage,
        sendState,
        errors,
    };
};
